/**
 * Authentication Role Constants
 *
 * Loaded from shared/auth-roles.json to stay in sync with backend.
 * Use these constants for role checks in the frontend.
 *
 * @example
 * import { AuthRoles } from '@/auth/AuthRoles'
 *
 * if (user.roles.includes(AuthRoles.ADMIN)) {
 *   // Show admin UI
 * }
 *
 * // In viewProps for route protection:
 * export const viewProps: ViewProps = {
 *   auth_roles: [AuthRoles.ADMIN, AuthRoles.SUPERVISOR]
 * }
 */

import roles from '../../../shared/auth-roles.json'

export const AuthRoles = {
  /** No authentication required */
  PUBLIC: roles.PUBLIC,

  /** Any authenticated user (no specific role required) */
  ANY: roles.ANY,

  /** System administrator - full access */
  ADMIN: roles.ADMIN,

  /** Training coach - views assessments/reports for assigned supervisors' users */
  TRAINING_COACH: roles.TRAINING_COACH,

  /** Supervisor - can manage assigned users */
  SUPERVISOR: roles.SUPERVISOR,
} as const

/** Type for role values */
export type AuthRole = (typeof AuthRoles)[keyof typeof AuthRoles]

/** Check if user has any of the specified roles */
export function hasRole(userRoles: string[] | undefined, requiredRoles: string[]): boolean {
  if (!userRoles) return false

  // PUBLIC means no auth required - always true
  if (requiredRoles.includes(AuthRoles.PUBLIC)) return true

  // ANY means any authenticated user - just need to have a roles array
  if (requiredRoles.includes(AuthRoles.ANY)) return true

  // Check if user has at least one of the required roles
  return requiredRoles.some((role) => userRoles.includes(role))
}

/** Check if user is an admin */
export function isAdmin(userRoles: string[] | undefined): boolean {
  return userRoles?.includes(AuthRoles.ADMIN) ?? false
}

/** Check if user is a training coach */
export function isTrainingCoach(userRoles: string[] | undefined): boolean {
  return userRoles?.includes(AuthRoles.TRAINING_COACH) ?? false
}

/** Check if user is a supervisor */
export function isSupervisor(userRoles: string[] | undefined): boolean {
  return userRoles?.includes(AuthRoles.SUPERVISOR) ?? false
}
